import { type LatLngTuple } from 'leaflet';

// IGN Géoplateforme route service (Valhalla engine on the BD TOPO — the `bdtopo-osrm` resource was withdrawn
// in 11/2026, same request and response shape). Public, no API key.
// Docs: https://data.geopf.fr/navigation/openapi/ — quota of 5 req/s per IP (→ the caller caches).
const ENDPOINT = 'https://data.geopf.fr/navigation/itineraire';

export type DrivingRoute = {
    // Road geometry ready for Leaflet ([lat, lng]).
    positions: LatLngTuple[];
    distanceMeters: number;
    durationSeconds: number;
};

// The API expects "lng,lat" points (the reverse of Leaflet).
const toLngLat = (point: LatLngTuple): string => `${point[1]},${point[0]}`;

// GET URL for the fastest car route through every stop, in the given order. Built by hand (not via
// URLSearchParams) to keep the "," and "|" separators literal as the API expects. Pure (no I/O) → testable.
// Intermediate stops are joined with "|".
export const buildItineraireUrl = (stops: LatLngTuple[]): string => {
    const start = toLngLat(stops[0]);
    const end = toLngLat(stops[stops.length - 1]);
    const intermediates = stops.slice(1, -1).map(toLngLat).join('|');
    const query =
        'resource=bdtopo-valhalla&profile=car&optimization=fastest' +
        `&start=${start}&end=${end}` +
        (intermediates ? `&intermediates=${intermediates}` : '') +
        '&geometryFormat=geojson&crs=EPSG:4326&distanceUnit=meter&timeUnit=second';
    return `${ENDPOINT}?${query}`;
};

type ItineraireResponse = {
    // GeoJSON LineString: coordinates as [lng, lat].
    geometry?: { coordinates?: [number, number][] };
    distance?: number;
    duration?: number;
};

// Real road geometry linking the stops (≥ 2) in the given order. Returns null on any failure
// (HTTP, network, abort, parse, empty geometry) — the caller then falls back to the straight line.
export const fetchDrivingRoute = async (stops: LatLngTuple[], signal?: AbortSignal): Promise<DrivingRoute | null> => {
    if (stops.length < 2) return null;
    try {
        const response = await fetch(buildItineraireUrl(stops), { signal });
        if (!response.ok) return null;
        const data: ItineraireResponse = await response.json();
        const coordinates = data.geometry?.coordinates;
        if (!coordinates?.length || data.distance == null || data.duration == null) return null;
        return {
            positions: coordinates.map(([lng, lat]): LatLngTuple => [lat, lng]),
            distanceMeters: data.distance,
            durationSeconds: data.duration,
        };
    } catch {
        // Network down, aborted request, or invalid JSON: silent fallback to the straight line.
        return null;
    }
};
